import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { Grid, Card, Typography, Button, TextField, IconButton, CircularProgress, InputAdornment } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Helmet } from 'react-helmet-async';
import { useTheme, styled } from '@mui/material/styles';
import { TreeView } from '@mui/x-tree-view/TreeView';
import { TreeItem } from '@mui/x-tree-view/TreeItem';
import { notification } from 'antd';
import FolderIcon from '@mui/icons-material/Folder';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import RefreshIcon from '@mui/icons-material/Refresh';
import dayjs from 'dayjs';
import { makeStyles } from '@mui/styles';
import Autocomplete from '@mui/material/Autocomplete';
import TodayIcon from '@mui/icons-material/Today';
import { useNavigate } from 'react-router-dom';
import { DemoContainer } from '@mui/x-date-pickers/internals/demo';
import { useTranslation } from 'react-i18next';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { SmartshileService } from './service';
import { getAclMenu, getToken } from '../../utils/common';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import GenerateDialog from './dialogpreview';
import logger from '../../utils/logger';

const useStyles = makeStyles({
  scrollable: {
    maxHeight: 500,
    overflowY: 'auto',
    width: 'inherit',
  },
  root: {
    '&:hover': {
      backgroundColor: 'yellow',
    },
  },
  icon: {
    marginRight: 5,
  },
});

// Helper function to add child to node
const addChildToNode = (nodes, parentId, newData, { replace = false } = {}) => {
  // Check if nodes is valid and iterable
  if (!nodes || !Array.isArray(nodes)) {
    return [];
  }

  const result = nodes.map((node) => {
    if (node && node.id === parentId) {
      const existingChildren = Array.isArray(node.children) ? node.children : [];
      const baseChildren = replace ? [] : existingChildren;
      const seenIds = new Set(baseChildren.map((child) => child.id));
      const normalizedChildren = newData
        .map((item) => ({
          idparrent: parentId,
          ...item,
        }))
        .filter((child) => !seenIds.has(child.id));
      const updatedNode = {
        ...node,
        children: [...baseChildren, ...normalizedChildren],
      };
      return updatedNode;
    }
    if (node?.children && Array.isArray(node.children) && node.children.length > 0) {
      return { ...node, children: addChildToNode(node.children, parentId, newData, { replace }) };
    }
    return node;
  });
  return result;
};

const mergeNodeLists = (existing = [], incoming = [], { replace = false } = {}) => {
  if (replace) {
    return incoming.map((item) => ({ ...item }));
  }
  const seen = new Set(existing.map((item) => item.id));
  const filtered = incoming.filter((item) => !seen.has(item.id));
  return [...existing, ...filtered];
};

const ROOT_NODE_KEY = 'root';
const PAGE_SIZE = 50;

export default function Dataset() {
  const history = useNavigate();
  const theme = useTheme();
  const classes = useStyles();
  const { t } = useTranslation();
  const { getDataSet } = useMemo(() => SmartshileService(), []);

  const [parent, setParent] = useState([]);
  const [record] = useState([]);
  const [recorddetail, setRecordDetail] = useState([]);
  const [orderlist, setOrderList] = useState([]);
  const [focusedItemId, setFocusedItemId] = useState(null);
  const [tempdata, settempdata] = useState([]);
  const [isloadingrefresh, setisloadingrefresh] = useState(false);
  const [isdialogopen, setisdialogopen] = useState(false);
  const [viewdata, setViewData] = useState(false);
  const [selectedData, setSelectedData] = useState('');
  const [lastFolderId, setLastFolderId] = useState(null);

  const [filter, setFilter] = useState('');
  const [lockFilter, setLockFilter] = useState('');
  const [nodePagination, setNodePagination] = useState({});
  const [loadingNodes, setLoadingNodes] = useState({});

  // NEW: loading + abort + requestId for record fetching (file details)
  const [isRecordLoading, setIsRecordLoading] = useState(false);
  const recordAbortRef = useRef(null);
  const recordReqIdRef = useRef(0);

  const scrollableRef = useRef(null);
  const treeContainerRef = useRef(null);
  const nodePaginationRef = useRef(nodePagination);

  useEffect(() => {
    nodePaginationRef.current = nodePagination;
  }, [nodePagination]);

  const inquiryAccountNumber = useCallback(
    async (id, page = 1, { recreate = false, search = '' } = {}) => {
      const nodeKey = id !== undefined && id !== null ? String(id) : ROOT_NODE_KEY;
      const requestedPage = Math.max(page, 1);
      const existingInfo = nodePaginationRef.current[nodeKey];
      if (!recreate && existingInfo && requestedPage <= (existingInfo.page || 0)) {
        return [];
      }

      setLoadingNodes((curr) => ({ ...curr, [nodeKey]: true }));
      try {
        const res = await getDataSet(id, getToken(), requestedPage, PAGE_SIZE, search);
        const entries = res?.data ?? [];
        // Keep fetching until we get 0 items - API returns totalCount as current page count
        const hasMorePages = entries.length > 0;
        const currentLoaded = (nodePaginationRef.current[nodeKey]?.loaded || 0) + entries.length;
        // If we got any data, set total to loaded + 1 to allow next page fetch; if 0 data, we're done
        const totalCount = hasMorePages ? currentLoaded + 1 : currentLoaded;

        setParent((prev) => {
          if (id === undefined || id === null) {
            return mergeNodeLists(prev, entries, { replace: recreate || requestedPage === 1 });
          }
          return addChildToNode(prev, id, entries, { replace: recreate || requestedPage === 1 });
        });

        let normalizedCount = 0;
        settempdata((curr) => {
          const base = recreate || requestedPage === 1 ? curr.filter((item) => item.idparrent !== nodeKey) : curr;
          const existingIds = new Set(base.filter((item) => item.idparrent === nodeKey).map((item) => item.id));
          const normalized = entries
            .map((item) => ({
              idparrent: nodeKey,
              ...item,
            }))
            .filter((item) => !existingIds.has(item.id));
          normalizedCount = normalized.length;
          return [...base, ...normalized];
        });

        setNodePagination((prev) => {
          const prevLoaded = recreate || requestedPage === 1 ? 0 : prev[nodeKey]?.loaded || 0;
          const effectiveNewItems = id === undefined || id === null ? entries.length : normalizedCount;
          const nextLoaded = recreate || requestedPage === 1 ? effectiveNewItems : prevLoaded + effectiveNewItems;
          return {
            ...prev,
            [nodeKey]: {
              page: requestedPage,
              total: totalCount,
              loaded: Math.min(totalCount, nextLoaded),
            },
          };
        });

        return entries;
      } catch (error) {
        logger.error('Error fetching dataset records:', error);
        throw error;
      } finally {
        setLoadingNodes((curr) => ({ ...curr, [nodeKey]: false }));
      }
    },
    [getDataSet, getToken]
  );

  const [dialogUploadPreview, setDialogUploadPreview] = React.useState({
    open: false,
    base64String: '',
    type: '',
    handleClose: () => {
      setDialogUploadPreview((prevState) => ({ ...prevState, open: !prevState.open }));
    },
    path: '',
    filename: '',
  });

  const safeAcl = typeof getAclMenu === 'function' ? getAclMenu('data_set') : { verify: true };
  const [acl] = useState(safeAcl);

  const [stateTab2, setstateTab2] = useState({
    id: '',
    parentName: '',
    subParent: '',
    edges: [],
    changes: '',
    createdAt: '',
    action: '',
    schedule: '',
    repeatOn: 'Sunday',
    dailyTime: '',
    monthlyDate: '',
    monthlyTime: '',
    monthlyLastDate: '',
    monthlyLastTime: '',
    datadetail: '',
    namepath: '',
    action_id: '',
    totalfolder: 0,
    totalfile: 0,
    data: null,
    lastType: null,
  });

  // ---------------- utils & helpers ----------------
  const handleFilterChange = (event) => {
    setFilter(event.target.value);
  };

  const handleScroll = useCallback(() => {
    const element = scrollableRef.current;
    if (!element) return;
    if (element.scrollHeight - element.scrollTop > element.clientHeight + 1) {
      return;
    }
    const activeKey = lastFolderId ?? ROOT_NODE_KEY;
    const info = nodePaginationRef.current[activeKey];
    if (!info || info.loaded >= (info.total || 0)) {
      return;
    }
    if (loadingNodes[activeKey]) {
      return;
    }
    const nextPage = (info.page || 1) + 1;
    inquiryAccountNumber(lastFolderId, nextPage, { search: lockFilter });
  }, [lastFolderId, loadingNodes, lockFilter, inquiryAccountNumber]);

  useEffect(() => {
    const element = scrollableRef.current;
    if (element) element.addEventListener('scroll', handleScroll);
    return () => {
      if (element) element.removeEventListener('scroll', handleScroll);
    };
  }, [handleScroll]);

  const fetchInfo3 = (url, setdata, signal) =>
    fetch(url, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
      signal,
    })
      .then((res) => res.json())
      .then((d) => {
        if (d.code !== 401 && d.code !== 500) setdata(d);
      });

  const fetchInfo6 = (url, setdata, signal) =>
    fetch(url, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
      signal,
    })
      .then((res) => res.json())
      .then((d) => {
        if (d.code !== 401 && d.code !== 500) {
          if (d.after === undefined) {
            setdata(d);
            fetchImage(d);
          } else {
            // Preserve original record metadata while normalizing data
            const normalized = {
              ...d, // Keep original record metadata (recordId, hash, etc.)
              data: d.after,
            };
            setdata(normalized);
            fetchImage(normalized);
          }
        }
      });

  const datasetrecord = (url, setdata, signal) =>
    fetch(url, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
      signal,
    })
      .then((res) => res.json())
      .then((d) => {
        if (d.code !== 401 && d.code !== 500) {
          // Handle new API structure: { data: [], totalCount: number }
          if (d && typeof d === 'object' && Array.isArray(d.data)) {
            setdata({ data: d.data, totalCount: d.totalCount || 0 });
          } else if (Array.isArray(d)) {
            // Legacy API structure: direct array
            setdata({ data: d, totalCount: d.length });
          } else {
            setdata(d);
          }
        }
      });

  const fetchImage = async (data) => {
    const entries = Object.entries(data?.data || {});
    await Promise.all(
      entries.map(async ([key, file]) => {
        const path = file?.metaData?.storageMetaData?.path;
        if (!path) return;
        const fileUrl = `${globalThis.REACT_APP_API_PURE}record/download/file/${encodeURIComponent(
          path.replaceAll('/', '%2F')
        )}/thumbnail`;
        try {
          const response = await fetch(fileUrl, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
          });
          if (!response.ok) return;
          await response.blob();
        } catch (error) {
          logger.error('Error fetching file:', error);
        }
      })
    );
  };

  notification.config({ placement: 'top', duration: 5 });

  const activeNodeKey = lastFolderId ?? ROOT_NODE_KEY;
  const activePagination = nodePagination[activeNodeKey];
  const canLoadMoreRecords = !!activePagination && activePagination.loaded < (activePagination.total || 0);
  const isActiveNodeLoading = !!loadingNodes[activeNodeKey];

  // Infinite scroll handler for tree container
  const handleTreeScroll = useCallback(
    (e) => {
      const { scrollTop, scrollHeight, clientHeight } = e.target;
      const isNearBottom = scrollHeight - scrollTop - clientHeight < 100;
      if (isNearBottom && canLoadMoreRecords && !isActiveNodeLoading) {
        inquiryAccountNumber(lastFolderId, (activePagination?.page || 1) + 1, { search: lockFilter });
      }
    },
    [canLoadMoreRecords, isActiveNodeLoading, lastFolderId, activePagination, lockFilter, inquiryAccountNumber]
  );

  const resetTreeState = useCallback(() => {
    setParent([]);
    settempdata([]);
    setNodePagination({});
    setLoadingNodes({});
    setLastFolderId(null);
    setstateTab2({});
    setSelectedData('');
  }, []);

  const handleSearchSubmit = useCallback(
    async (query) => {
      const trimmedQuery = query.trim();
      setisloadingrefresh(true);
      setLockFilter(trimmedQuery);
      resetTreeState();
      try {
        await inquiryAccountNumber(null, 1, { recreate: true, search: trimmedQuery });
      } finally {
        setisloadingrefresh(false);
      }
    },
    [inquiryAccountNumber, resetTreeState]
  );

  const [isNotificationActive, setIsNotificationActive] = useState(false);

  const addSchedule = (url, info) =>
    fetch(url, {
      method: 'POST',
      body: JSON.stringify({
        title: findNodePath(parent, stateTab2.namepath?.split(' > ')[stateTab2.namepath?.split(' > ').length - 1]),
time_start:
  stateTab2.dailyTime == null || stateTab2.dailyTime === ''
    ? dayjs().subtract(7, 'hour').format('YYYY-MM-DD hh:mm:ss A')
    : dayjs(stateTab2.dailyTime).subtract(7, 'hour').format('YYYY-MM-DD hh:mm:ss A'),
time_end:
  stateTab2.dailyTime == null || stateTab2.dailyTime === ''
    ? dayjs().subtract(7, 'hour').format('YYYY-MM-DD hh:mm:ss A')
    : dayjs(stateTab2.dailyTime).subtract(7, 'hour').format('YYYY-MM-DD hh:mm:ss A'),
        schedule: stateTab2.schedule || '',
        repeat_on: '',
        dataset_id: selectedData?.type === 'folder' ? selectedData.id : '',
        record_id: selectedData?.type === 'file' ? selectedData.id : '',
        parent_name: stateTab2.parentName,
      }),
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
    })
      .then((res) => res.json())
      .then((d) => {
        if (d.response_code === 200) {
          notification.success({
            message: 'Success',
            description: t('VerificationPrompt.1'),
            duration: 5,
            onClose: () => setIsNotificationActive(false),
          });
        } else if (d.error_message === 'There is no acction assosiate with this dataset please add action first') {
          notification.error({
            message: 'Error',
            description: t('Please define an action for the dataset before requesting verification'),
            placement: 'top',
            duration: 5,
          });
        } else {
          notification.error({
            message: 'Error',
            description: t('There is error while processing the schedule, please contact Super Admin'),
            placement: 'top',
            duration: 5,
          });
        }
        if (info === '') history('/dashboard/calendar');
      });

  useEffect(() => {
    handleSearchSubmit('');
  }, [handleSearchSubmit]);

  // Console log parent state changes
  useEffect(() => {}, [parent]);

  const GenerateLabel = ({ tittle, value, variant = 'subtittle2' }) => (
    <Grid item xs={12} container direction={'row'}>
      <Grid item xs={6}>
        <Typography variant="subtittle2">{tittle}</Typography>
      </Grid>
      <Grid item xs={1}>
        <Typography variant="subtittle2">:</Typography>
      </Grid>
      <Grid item>
        <Typography variant={variant}>{value}</Typography>
      </Grid>
    </Grid>
  );

  GenerateLabel.propTypes = {
    tittle: PropTypes.string,
    value: PropTypes.string,
    variant: PropTypes.string,
  };

  const GenerateTime = () => (
    <Grid item xs={6} style={{ paddingTop: 8 }}>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DemoContainer components={['TimePicker']}>
          <TimePicker
            value={stateTab2.dailyTime && dayjs(stateTab2.dailyTime).isValid() ? dayjs(stateTab2.dailyTime) : null}
            onChange={(newValue) => {
              // Preserve existing date if available, otherwise use today's date
              let formattedDateTime;
              if (stateTab2.dailyTime && dayjs(stateTab2.dailyTime).isValid()) {
                // Extract date from existing dailyTime
                const existingDateTime = dayjs(stateTab2.dailyTime);
                // Combine existing date with new time
                formattedDateTime = existingDateTime
                  .hour(newValue.hour())
                  .minute(newValue.minute())
                  .second(newValue.second())
                  .format('YYYY-MM-DD hh:mm:ss A');
              } else {
                // If no existing date, use today's date with new time
                formattedDateTime = newValue.format('YYYY-MM-DD hh:mm:ss A');
              }
              setstateTab2({ ...stateTab2, dailyTime: formattedDateTime });
            }}
            sx={{
              '& input': { marginTop: 0, paddingTop: 1, paddingBottom: 1, minWidth: 0 },
            }}
            placeholder="Empty"
          />
        </DemoContainer>
      </LocalizationProvider>
    </Grid>
  );

  const GenerateAdditional = () => {
    if (stateTab2.schedule === 'Weekly' || stateTab2.schedule === 'Monthly') {
      return (
        <Grid item container direction={'row'} xs={12} justifyContent={'space-around'}>
          <Grid item xs={3} alignSelf={'center'}>
            <Typography variant="subtittle2">{t('StartDate.1')}</Typography>
          </Grid>
          <Grid item xs={8}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DemoContainer components={['DatePicker']}>
                <DatePicker
                  disablePast
                  ampm={false}
                  fullWidth
                  value={
                    stateTab2.dailyTime && dayjs(stateTab2.dailyTime).isValid() ? dayjs(stateTab2.dailyTime) : null
                  }
                  onChange={(newValue) => {
                    // Preserve existing time if available, otherwise use default time (00:00:00)
                    let formattedDateTime;
                    if (stateTab2.dailyTime && dayjs(stateTab2.dailyTime).isValid()) {
                      // Extract time from existing dailyTime
                      const existingDateTime = dayjs(stateTab2.dailyTime);
                      // Combine new date with existing time
                      formattedDateTime = newValue
                        .hour(existingDateTime.hour())
                        .minute(existingDateTime.minute())
                        .second(existingDateTime.second())
                        .format('YYYY-MM-DD hh:mm:ss A');
                    } else {
                      // If no existing time, use default time (00:00:00)
                      formattedDateTime = newValue.format('YYYY-MM-DD hh:mm:ss A');
                    }
                    setstateTab2({ ...stateTab2, dailyTime: formattedDateTime });
                  }}
                  format="YYYY-MM-DD"
                  sx={{
                    '& input': { marginTop: 0, paddingTop: 1, paddingBottom: 1, minWidth: 0 },
                  }}
                />
              </DemoContainer>
            </LocalizationProvider>
          </Grid>
        </Grid>
      );
    }
    return null;
  };

  const handleFocus = (event, nodeId) => {
    setFocusedItemId(nodeId);
  };

  const loadRecord = useCallback(async (fileId) => {
    recordAbortRef.current?.abort?.();
    const controller = new AbortController();
    recordAbortRef.current = controller;
    const { signal } = controller;

    recordReqIdRef.current += 1;
    const myReqId = recordReqIdRef.current;

    setIsRecordLoading(true);
    setViewData(false);
    setOrderList([]);
    setRecordDetail({});

    try {
      const deltaPromise = fetchInfo3(
        `${globalThis.REACT_APP_API_PURE}record/delta/${fileId}`,
        (d) => {
          if (signal.aborted || myReqId !== recordReqIdRef.current) return;
          setOrderList(d);
        },
        signal
      );

      const detailPromise = fetchInfo6(
        `${globalThis.REACT_APP_API_PURE}record/${fileId}`,
        (d) => {
          if (signal.aborted || myReqId !== recordReqIdRef.current) return;
          setRecordDetail(d);
        },
        signal
      );

      await Promise.all([deltaPromise, detailPromise]);

      if (!signal.aborted && myReqId === recordReqIdRef.current) {
        setIsRecordLoading(false);
        setViewData(true);
      }
    } catch (e) {
      if (e?.name !== 'AbortError') {
        logger.error('Error in dataset:', e);
      }
      if (!signal.aborted && myReqId === recordReqIdRef.current) {
        setIsRecordLoading(false);
        setViewData(false);
      }
    }
  }, []);

  function GenerateNewTreeFile({ data, namaNode }) {
    return data?.length ? (
      data?.map((data2) => (
        <CustomTreeItem
          itemID={data2?.id}
          key={data2?.id}
          focused={focusedItemId === data2?.id}
          onFocus={(event) => handleFocus(event, data2?.id)}
          onClick={() => {
            if (data2?.type === 'folder') {
              recordAbortRef.current?.abort?.();
              setIsRecordLoading(false);
              setViewData(false);
              setOrderList([]);
              setRecordDetail({});

              inquiryAccountNumber(data2.id, 1, { recreate: true, search: lockFilter });
              setLastFolderId(data2.id);
              const newNamepath = `${namaNode} > ${data2.name}`;
              setstateTab2((prev) => ({
                ...prev,
                namepath: newNamepath,
                subParent: data2.name,
                data: data2,
                lastType: data2?.type,
              }));
            } else {
              setViewData(false);
              loadRecord(data2.id);
              let name = `${namaNode} > ${data2.name}`;
              if (stateTab2.lastType !== 'folder') {
                const parentPath = stateTab2?.namepath?.split(' > ')?.slice(0, -1)?.join(' > ') || '';
                name = `${parentPath} > ${data2?.name}`;
              }
              setstateTab2((prev) => ({
                ...prev,
                namepath: name,
                subParent: data2.name,
                data: data2,
                lastType: data2?.type,
              }));
            }
            handleFocus(null, data2.id);
            setSelectedData(data2);
          }}
          label={
            <Grid container direction={'row'} spacing={1}>
              <Grid item>
                {data2?.type !== 'folder' ? (
                  <InsertDriveFileIcon style={{ color: GenerateColor(data2) }} />
                ) : (
                  <FolderIcon style={{ color: GenerateColor(data2) }} />
                )}
              </Grid>
              <Grid item alignSelf={'end'}>
                <Typography variant="body2">{data2.name}</Typography>
              </Grid>
            </Grid>
          }
        >
          {data2?.data?.recordCount > 0
            ? GenerateNewTreeFile({
                data: tempdata?.filter((item) => item?.data?.parentId === data2.id),
                namaNode: namaNode + data2.name,
              })
            : ''}
        </CustomTreeItem>
      ))
    ) : data?.some((item) => item?.type !== 'folder') ? (
      <CircularProgress />
    ) : (
      <span />
    );
  }

  GenerateNewTreeFile.propTypes = {
    data: PropTypes.array,
    namaNode: PropTypes.string,
  };

  const GenerateColor = (data) => {
    if (data?.openAlertCount > 0) return theme.palette.error.main;
    if (data?.inProgressAlertCount > 0) return theme.palette.warning.main;
    if (data?.pendingApprovalAlertCount > 0) return theme.palette.info.main;
    if (data?.pendingRollbackAlertCount > 0) return theme.palette.primary.main;
    return 'green';
  };

  const findNodePath = (nodes, targetName, path = []) => {
    // Check if nodes is valid and iterable
    if (!nodes || !Array.isArray(nodes) || nodes.length === 0) {
      return null;
    }

    for (const node of nodes) {
      if (!node || !node.name) continue; // Skip invalid nodes

      const currentPath = [...path, node.name];
      if (node.name === targetName) return currentPath.join(' > ');
      if (node.children && Array.isArray(node.children) && node.children.length > 0) {
        const result = findNodePath(node.children, targetName, currentPath);
        if (result) return result;
      }
    }
    return null;
  };

  const renderTree = (nodes, first, namaNode) =>
    nodes?.type === 'file' && !first ? (
      ''
    ) : (
      <TreeItem
        key={nodes?.id}
        nodeId={nodes?.id}
        label={
          <Grid container direction={'row'} spacing={1} alignItems="center">
            <Grid item>
              {nodes?.type !== 'folder' ? (
                <InsertDriveFileIcon style={{ color: GenerateColor(nodes) }} />
              ) : (
                <FolderIcon style={{ color: GenerateColor(nodes) }} />
              )}
            </Grid>
            <Grid item>
              <Typography variant="body2">{nodes?.name}</Typography>
            </Grid>
          </Grid>
        }
        onClick={() => {
          // Check if this is actually a top-level folder being clicked
          // (not a nested folder within another folder)
          const isTopLevelFolder = !nodes?.idparrent || nodes?.idparrent === '';

          if (first || isTopLevelFolder) {
            // This is a top-level folder, treat it as such
            if (nodes?.type === 'folder') {
              recordAbortRef.current?.abort?.();
              setIsRecordLoading(false);
              setViewData(false);
              setOrderList([]);
              setRecordDetail({});

              inquiryAccountNumber(nodes?.id, 1, { recreate: true, search: lockFilter });
              handleFocus(null, nodes?.id);
              setSelectedData(nodes);
              const newNamepath = nodes?.name;
              setstateTab2((prev) => ({
                ...prev,
                namepath: newNamepath,
                parentName: nodes?.name,
                subParent: nodes?.name,
                data: nodes,
                lastType: nodes?.type,
              }));
              setLastFolderId(nodes?.id);
            } else {
              handleFocus(null, nodes?.id);
              setSelectedData(nodes);

              const newNamepath = `${namaNode} > ${nodes?.name}`;
              setstateTab2((prev) => ({
                ...prev,
                namepath: newNamepath,
                parentName: nodes?.name,
                subParent: nodes?.name,
                data: nodes,
                lastType: nodes?.type,
              }));

              setViewData(false);
              loadRecord(nodes?.id);
            }
          } else {
            if (nodes?.type === 'folder') {
              recordAbortRef.current?.abort?.();
              setIsRecordLoading(false);
              setViewData(false);
              setOrderList([]);
              setRecordDetail({});

              inquiryAccountNumber(nodes?.id, 1, { recreate: true, search: lockFilter });
              setLastFolderId(nodes?.id);
            } else {
              setViewData(false);
              loadRecord(nodes?.id);
            }
            handleFocus(null, nodes?.id);
            setSelectedData(nodes);

            // Check if we're switching parent contexts
            const currentParentName = stateTab2.namepath?.split(' > ')[0];
            const newParentName = namaNode;

            let newNamepath;
            if (currentParentName !== newParentName) {
              // We're switching to a different parent context, treat as top-level
              newNamepath = nodes?.name;
            } else {
              // This is truly a nested folder within the same parent
              newNamepath = `${stateTab2.namepath} > ${nodes?.name}`;
            }
            setstateTab2((prev) => ({
              ...prev,
              namepath: newNamepath,
              subParent: nodes?.name,
              data: nodes,
              lastType: nodes?.type,
            }));
          }
        }}
      >
        {Array.isArray(nodes?.children) ? (
          nodes?.children.map((node) => renderTree(node, false, nodes?.name))
        ) : nodes?.data?.recordCount > 0 ? (
          <span />
        ) : null}
      </TreeItem>
    );

  const CustomTreeItem = styled(TreeItem)(({ theme, focused }) => ({
    ...(focused && {
      backgroundColor: theme.palette.action.selected,
      '&:hover': { backgroundColor: theme.palette.action.hover },
    }),
  }));

  const isFileSelected = selectedData && selectedData.type === 'file';
  const canView =
    isFileSelected &&
    !isRecordLoading &&
    viewdata &&
    Array.isArray(orderlist) &&
    orderlist.length > 0 &&
    !!recorddetail?.data;

  useEffect(
    () => () => {
      recordAbortRef.current?.abort?.();
    },
    []
  );

  return (
    <span>
      <Helmet>
        <title>Dataset Map | COVAS</title>
      </Helmet>
      <Card style={{ marginTop: 20, minHeight: '80vh', maxHeight: '80vh', overflow: 'auto' }}>
        <Grid container direction={'row'}>
          <GenerateDialog
            open={isdialogopen}
            onClose={() => setisdialogopen(false)}
            t={t}
            stateTab2={stateTab2}
            record={record}
            orderlist={orderlist}
            recorddetail={recorddetail}
            dialogUploadPreview={dialogUploadPreview}
            setDialogUploadPreview={setDialogUploadPreview}
            getToken={getToken}
            baseUrl={globalThis.REACT_APP_API_PURE}
          />

          <Grid item xs={12} style={{ borderBottom: '1px solid #D1D1D1' }}>
            <Typography variant="h5" style={{ color: 'grey', padding: 20 }}>
              Dataset{' '}
              {findNodePath(parent, stateTab2.namepath?.split(' > ')[stateTab2.namepath?.split(' > ').length - 1]) ||
                ''}
            </Typography>
          </Grid>

          {/* Left column (tree) */}
          <Grid item xs={4} style={{ borderRight: '1px solid #D1D1D1', padding: 10 }} container direction={'row'}>
            <div style={{ width: '-webkit-fill-available' }}>
              <Grid container direction={'row'}>
                <Grid
                  item
                  style={{ paddingTop: 20, paddingLeft: 20 }}
                  xs={12}
                  container
                  direction={'row'}
                  alignItems={'center'}
                >
                  <Grid item container direction={'row'} xs={12} justifyContent={'space-between'}>
                    <Grid item>
                      <Typography variant="h5">Datasets</Typography>
                    </Grid>
                    <Grid item>
                      <TextField
                        label="Search"
                        variant="outlined"
                        value={filter}
                        size="small"
                        onChange={handleFilterChange}
                        style={{ marginBottom: '20px' }}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter') {
                            handleSearchSubmit(filter);
                          }
                        }}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              {lockFilter === filter && filter !== '' ? (
                                <IconButton
                                  onClick={() => {
                                    setFilter('');
                                    handleSearchSubmit('');
                                  }}
                                >
                                  <RefreshIcon />
                                </IconButton>
                              ) : (
                                <IconButton
                                  onClick={() => {
                                    handleSearchSubmit(filter);
                                  }}
                                >
                                  <SearchIcon />
                                </IconButton>
                              )}
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                  </Grid>
                </Grid>

                <Grid
                  item
                  xs={12}
                  style={{ paddingLeft: 20, paddingTop: 20, maxHeight: 500, overflowY: 'auto' }}
                  ref={treeContainerRef}
                  onScroll={handleTreeScroll}
                >
                  {isloadingrefresh ? (
                    <CircularProgress />
                  ) : (
                    <TreeView defaultCollapseIcon={<ExpandMoreIcon />} defaultExpandIcon={<ChevronRightIcon />}>
                      {Array.isArray(parent) && parent.length > 0
                        ? parent.map((node, _i) => renderTree(node, true, node.name))
                        : null}
                    </TreeView>
                  )}
                  {isActiveNodeLoading && canLoadMoreRecords && (
                    <Grid item xs={12} style={{ paddingTop: 12, textAlign: 'center' }}>
                      <CircularProgress size={24} />
                    </Grid>
                  )}
                </Grid>
              </Grid>
            </div>
          </Grid>

          {/* Middle column (records in folder) */}
          <Grid item xs={4} style={{ borderRight: '1px solid #D1D1D1' }} container direction={'row'}>
            <Grid item xs={12}>
              {selectedData === '' ? (
                ''
              ) : (
                <div style={{ paddingTop: 20 }}>
                  <Grid container direction={'row'} spacing={2}>
                    <Grid item xs={12}>
                      <Typography variant="h5" style={{ paddingLeft: 10 }}>
                        Records
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography style={{ padding: 15, backgroundColor: '#F1FAFE' }} variant="h6">
                        {(() => {
                          const p = findNodePath(
                            parent,
                            stateTab2.namepath?.split(' > ')[stateTab2.namepath?.split(' > ').length - 1]
                          );
                          if (!p) return '';
                          if (p.includes(' > ')) return p.split(' > ')[0];
                          return p.trim();
                        })()}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography style={{ padding: 20, borderBottom: '1px solid #E7E7E7' }}>
                        <FolderIcon style={{ color: GenerateColor(stateTab2.data) }} /> {stateTab2.subParent}
                      </Typography>
                    </Grid>

                    <Grid item xs={12} className={classes.scrollable} ref={scrollableRef}>
                      <TreeView>
                        {GenerateNewTreeFile({
                          data: tempdata?.filter((item) => item.idparrent === lastFolderId),
                          namaNode: stateTab2.namepath,
                          records: true,
                        })}
                      </TreeView>
                    </Grid>

                    {/* Load More button for records section */}
                    {canLoadMoreRecords && (
                      <Grid item xs={12} style={{ paddingTop: 12, textAlign: 'center', paddingBottom: 12 }}>
                        <Button
                          variant="outlined"
                          size="small"
                          disabled={isActiveNodeLoading}
                          onClick={() =>
                            inquiryAccountNumber(lastFolderId, (activePagination?.page || 1) + 1, {
                              search: lockFilter,
                            })
                          }
                        >
                          {isActiveNodeLoading
                            ? t('Loading.1', { defaultValue: 'Loading...' })
                            : t('LoadMore.1', { defaultValue: 'Load more' })}
                        </Button>
                      </Grid>
                    )}

                    {tempdata?.filter((item) => item.idparrent === selectedData.id).length === 0 ? (
                      <Grid item xs={12} style={{ marginTop: 0 }} container direction={'row'}>
                        {stateTab2?.edges?.map((data) => (
                          <Grid item xs={12} key={data.id}>
                            <Button
                              variant="text"
                              startIcon={<InsertDriveFileIcon style={{ color: '#1EB3D9' }} />}
                              onClick={() => {
                                setSelectedData({ id: data.id, type: 'file', name: data.id });
                                setViewData(false);
                                loadRecord(data.id);

                                const fixedpath = stateTab2.namepath.split(' > ').slice(0, -1).join(' > ');
                                setstateTab2((prev) => ({
                                  ...prev,
                                  datadetail: data,
                                  namepath: `${fixedpath || stateTab2.namepath} > ${data.id}`,
                                  parentName: data.id,
                                  totalfile: 1,
                                  totalfolder: 0,
                                  lastType: 'file',
                                }));
                              }}
                            >
                              {data.id}
                            </Button>
                          </Grid>
                        ))}
                      </Grid>
                    ) : null}
                  </Grid>
                </div>
              )}
            </Grid>
          </Grid>

          <Grid item xs={4}>
            {selectedData === '' ? (
              ''
            ) : (
              <div style={{ padding: 20 }}>
                <Grid container direction={'row'} spacing={2}>
                  <Grid item xs={12} container direction={'row'} spacing={2}>
                    <Grid item>
                      <Typography variant="h5">{t('Detail.1')}</Typography>
                    </Grid>
                  </Grid>

                  <Grid item xs={12} style={{ borderBottom: '1px solid #D1D1D1' }} />

                  <Grid item xs={12} container direction={'row'} style={{ marginLeft: 30, marginTop: 10 }}>
                    {GenerateLabel({
                      tittle: (() => {
                        // Get the parent name (first part of the path)
                        const pathParts = stateTab2.namepath?.split(' > ') || [];
                        const parentName = pathParts[0];
                        const isParentDeleted = parentName === 'Deleted';
                        const isChildSelected = pathParts.length > 1;

                        // Use findNodePath to check if this is actually a top-level dataset
                        const currentNodeName =
                          stateTab2.namepath?.split(' > ')[stateTab2.namepath?.split(' > ').length - 1];
                        const fullPath = findNodePath(parent, currentNodeName);
                        const isTopLevelDataset = fullPath && fullPath.split(' > ').length === 1;

                        // Check if we're directly selecting a top-level folder (not a child)
                        const isTopLevelSelection = pathParts.length === 1;

                        if (isTopLevelSelection) {
                          // Direct top-level selection
                          if (isTopLevelDataset) {
                            // This is a true top-level dataset
                            return isParentDeleted ? t('FolderName.1') : t('Dataset.1');
                          } else {
                            // This is a folder within a dataset, should be "Folder Name"
                            return t('FolderName.1');
                          }
                        } else if (!isChildSelected) {
                          // Parent folder selected (shouldn't happen with current logic, but safety check)
                          return isParentDeleted ? t('FolderName.1') : t('Dataset.1');
                        } else {
                          // Child selected
                          if (isParentDeleted) {
                            return t('FileId.1');
                          } else {
                            // Parent is not deleted, check child type
                            return selectedData?.type === 'file' ? t('FileName.1') : t('FolderName.1');
                          }
                        }
                      })(),
                      value: stateTab2.namepath?.split(' > ')[stateTab2.namepath?.split(' > ').length - 1],
                    })}
                    {GenerateLabel({
                      tittle: t('Parent.1'),
                      value: (() => {
                        const full = findNodePath(
                          parent,
                          stateTab2.namepath?.split(' > ')[stateTab2.namepath?.split(' > ').length - 1]
                        );
                        if (!full) return '';
                        if (!full.includes(' > ')) return '';
                        const parts = full.split(' > ');
                        return parts.at(-2) || '';
                      })(),
                    })}
                    {GenerateLabel({
                      tittle: t('Contains.1'),
                      value: `${selectedData?.data?.recordCount || 0} Records`,
                    })}
                  </Grid>

                  <Grid item xs={12} style={{ borderBottom: '1px solid #D1D1D1' }} />

                  <Grid item xs={12} container direction={'row'} style={{ marginLeft: 30, marginTop: 10 }}>
                    {GenerateLabel({
                      tittle: t('Changes.1'),
                      value: (selectedData?.authorizedCount || 0) + (selectedData?.unAuthorizedCount || 0),
                    })}
                  </Grid>

                  <Grid item xs={12} style={{ textAlignLast: 'right' }}>
                    <Typography variant="subtittle2" style={{ color: '#49C9EA', backgroundColor: '#F1FAFE' }}>
                      {selectedData?.authorizedCount || 0} {t('AuthorizedChanges.1')}
                    </Typography>
                    <div />
                    <Typography variant="subtittle2" style={{ color: '#F57A74', backgroundColor: '#FEF3F2' }}>
                      {selectedData?.unAuthorizedCount || 0} {t('UnauthorizedChanges.1')}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} textAlign={'right'}>
                    <Button
                      variant="outline"
                      disabled={!canView}
                      aria-busy={isRecordLoading ? 'true' : 'false'}
                      aria-live="polite"
                      style={{ backgroundColor: canView ? '#12A2CE' : 'gray', color: 'white' }}
                      onClick={() => {
                        if (!orderlist?.length) return;
                        setisdialogopen(true);
                        const last = orderlist.at(-1);
                        fetchInfo6(
                          `${globalThis.REACT_APP_API_PURE}record/compare/${last.recordId}/${last.txInfos.txId}`,
                          setRecordDetail
                        );
                      }}
                      startIcon={
                        isRecordLoading && isFileSelected ? (
                          <CircularProgress size={16} thickness={5} color="inherit" />
                        ) : null
                      }
                    >
                      {isRecordLoading && isFileSelected ? 'Loading…' : t('View.1')}
                    </Button>
                  </Grid>

                  <Grid item xs={12} container direction={'row'} style={{ marginLeft: 30, marginTop: 10 }}>
                    {GenerateLabel({ tittle: t('LatestUpdate.1'), value: stateTab2.createdAt, variant: 'caption' })}
                  </Grid>

                  <Grid item xs={12} style={{ borderBottom: '1px solid #D1D1D1' }} />

                  {acl.verify ? (
                    <Grid item xs={12} container direction={'row'}>
                      <Grid item container direction={'row'} xs={12} justifyContent={'space-between'}>
                        <Grid item>
                          <Typography variant="caption">
                            {new Date().toLocaleDateString('en-US', {
                              weekday: 'long',
                              month: 'long',
                              day: 'numeric',
                              year: 'numeric',
                            })}
                          </Typography>
                        </Grid>
                      </Grid>

                      <Grid item style={{ marginTop: 20 }}>
                        <TodayIcon style={{ color: '#12A2CE' }} />
                      </Grid>
                      <Grid item style={{ marginTop: 20 }}>
                        <Typography variant="subtittle2">{t('VerifSchedule.1')}</Typography>
                      </Grid>

                      <Grid item xs={12} container direction={'row'} spacing={2}>
                        <Grid item xs={6}>
                          <Autocomplete
                            fullWidth
                            size="small"
                            id={`combo-box-demo-`}
                            options={[{ label: 'Daily' }, { label: 'Weekly' }, { label: 'Monthly' }]}
                            placeholder={t('Empty.1')}
                            value={[{ label: 'Daily' }, { label: 'Weekly' }, { label: 'Monthly' }].find(
                              (e) => e.label === stateTab2.schedule
                            )}
                            onChange={(event, newValue, reason) => {
                              if (reason === 'clear') {
                                setstateTab2({ ...stateTab2, schedule: '' });
                              } else {
                                setstateTab2({ ...stateTab2, schedule: newValue?.label });
                              }
                            }}
                            renderInput={(params) => <TextField {...params} placeholder={'Empty'} />}
                          />
                        </Grid>
                        {GenerateTime()}
                        {GenerateAdditional()}
                      </Grid>

                      <Grid item xs={12} style={{ marginBottom: 20 }}>
                        <Button
                          disabled={
                            stateTab2.schedule == null ||
                            stateTab2.schedule === '' ||
                            stateTab2.dailyTime == null ||
                            stateTab2.dailyTime === '' ||
                            !dayjs(stateTab2.dailyTime).isValid() ||
                            isNotificationActive
                          }
                          fullWidth
                          style={{
                            backgroundColor:
                              stateTab2.schedule == null ||
                              stateTab2.schedule === '' ||
                              stateTab2.dailyTime == null ||
                              stateTab2.dailyTime === '' ||
                              !dayjs(stateTab2.dailyTime).isValid() ||
                              isNotificationActive
                                ? 'gray'
                                : '#12A2CE',
                            color: 'white',
                            marginTop: 50,
                          }}
                          onClick={() => {
                            setIsNotificationActive(true);
                            addSchedule(`${globalThis.REACT_APP_API_PURE}calendar/addcalendar`, '');
                          }}
                        >
                          {t('VerifSchedule.2')}
                        </Button>
                      </Grid>

                      <Grid item xs={12} style={{ borderBottom: '1px solid #D1D1D1' }} />

                      <Grid item xs={12}>
                        <Button
                          disabled={isNotificationActive}
                          fullWidth
                          style={{ backgroundColor: isNotificationActive ? 'gray' : '#12A2CE', color: 'white' }}
                          onClick={() => {
                            setIsNotificationActive(true);
                            if (stateTab2.schedule == null) {
                              setstateTab2((prev) => ({
                                ...prev,
                                schedule: 'Daily',
                                dailyTime: dayjs().format('YYYY-MM-DD '),
                              }));
                            }
                            addSchedule(`${globalThis.REACT_APP_API_PURE}calendar/addcalendar`, 'now');
                            setTimeout(() => {}, 4000);
                          }}
                        >
                          {t('VerifSchedule.3')}
                        </Button>
                      </Grid>
                    </Grid>
                  ) : null}
                </Grid>
              </div>
            )}
          </Grid>
        </Grid>
      </Card>
    </span>
  );
}
